import { useEffect, useState } from 'react';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';
import CircularProgress from '@material-ui/core/CircularProgress';
import { createFormField } from '@backstage/plugin-scaffolder-react/alpha';

type Vcn = { id: string; name: string; cidrBlock: string };

export function OciVcnPickerComponent({
  onChange,
  rawErrors,
  formData,
  formContext,
}: any) {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const fd = (formContext as any)?.formData ?? {};
  const region: string = fd.region ?? '';
  const compartmentId: string = fd.compartmentId ?? '';
  const [vcns, setVcns] = useState<Vcn[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!region || !compartmentId) {
      setVcns([]);
      onChange(undefined as any);
      return;
    }
    setLoading(true);
    setError('');
    discoveryApi.getBaseUrl('infrastructure').then(baseUrl => {
      fetchApi
        .fetch(
          `${baseUrl}/oci/vcns?region=${encodeURIComponent(
            region,
          )}&compartmentId=${encodeURIComponent(compartmentId)}`,
        )
        .then(r => r.json())
        .then(data => {
          if (data.error) {
            setError(
              typeof data.error === 'string'
                ? data.error
                : data.error?.message ?? 'Failed to load VCNs',
            );
          } else {
            setVcns(data.vcns ?? []);
            onChange(undefined as any);
          }
        })
        .catch(() => setError('Failed to load VCNs'))
        .finally(() => setLoading(false));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region, compartmentId]);

  if (loading) return <CircularProgress size={20} />;

  return (
    <FormControl fullWidth error={!!rawErrors?.length || !!error}>
      <InputLabel>VCN (Virtual Cloud Network)</InputLabel>
      <Select
        value={formData ?? ''}
        onChange={e => onChange(e.target.value as string)}
        disabled={!region || !compartmentId}
      >
        {(!region || !compartmentId) && (
          <MenuItem value="" disabled>
            Select region and compartment first
          </MenuItem>
        )}
        {vcns.map(v => (
          <MenuItem key={v.id} value={v.id}>
            {v.name} ({v.cidrBlock})
          </MenuItem>
        ))}
      </Select>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
}

export const OciVcnPickerField = createFormField({
  name: 'OciVcnPicker',
  component: OciVcnPickerComponent,
});
